import { createContext, useCallback, useContext, useState, type CSSProperties, type ReactNode } from 'react'

import {
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useRole,
    type FloatingContext,
    type UseFloatingReturn,
    type UseInteractionsReturn,
} from '@floating-ui/react'

import type { OverflowMenuProps } from './types'

type OverflowMenuContextValue = {
    open: boolean
    setOpen: (open: boolean) => void
    closeOnSelect: boolean
    refs: UseFloatingReturn['refs']
    floatingStyles: CSSProperties
    getReferenceProps: UseInteractionsReturn['getReferenceProps']
    getFloatingProps: UseInteractionsReturn['getFloatingProps']
    context: FloatingContext
}

const OverflowMenuContext = createContext<OverflowMenuContextValue | null>(null)

export function useOverflowMenuContext() {
    const ctx = useContext(OverflowMenuContext)
    if (!ctx) {
        throw new Error('OverflowMenu compound components must be used within <OverflowMenu>')
    }
    return ctx
}

type ProviderProps = Pick<
    OverflowMenuProps,
    'open' | 'defaultOpen' | 'onOpenChange' | 'closeOnSelect' | 'placement' | 'trigger'
> & {
    children?: ReactNode
}

export function OverflowMenuProvider({
    open: openProp,
    defaultOpen = false,
    onOpenChange,
    closeOnSelect = true,
    placement = 'bottom-start',
    trigger = 'click',
    children,
}: ProviderProps) {
    const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
    const isControlled = openProp !== undefined
    const open = isControlled ? openProp : uncontrolledOpen

    const setOpen = useCallback(
        (next: boolean) => {
            if (!isControlled) {
                setUncontrolledOpen(next)
            }
            onOpenChange?.(next)
        },
        [isControlled, onOpenChange]
    )

    const { refs, floatingStyles, context } = useFloating({
        placement,
        strategy: 'fixed',
        transform: false,
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        middleware: [offset(4), flip({ padding: 8, crossAxis: false }), shift({ padding: 8 })],
    })

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useClick(context, { enabled: trigger === 'click' }),
        useHover(context, { enabled: trigger === 'hover', move: false }),
        useDismiss(context),
        useRole(context, { role: 'menu' }),
    ])

    return (
        <OverflowMenuContext.Provider
            value={{
                open,
                setOpen,
                closeOnSelect,
                refs,
                floatingStyles,
                getReferenceProps,
                getFloatingProps,
                context,
            }}
        >
            {children}
        </OverflowMenuContext.Provider>
    )
}
